import React, { useState, useEffect, useMemo, useContext } from 'react';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Settings, Plus, Edit, Trash2, CheckCircle, FilterX, XCircle, AlertTriangle } from 'lucide-react';
import { QuotaCreateDialog } from './components/QuotaCreateDialog';
import { QuotaEditDialog } from './components/QuotaEditDialog';
import { QuotaDeleteDialog } from './components/QuotaDeleteDialog';
import { DataTable } from '../../components/DataTable';
import { AdminQuotasSkeleton } from './components/AdminQuotasSkeleton';
import { Skeleton } from '../../components/ui/skeleton';
import AppContext from '../../contexts/AppContext';
import { hasUserPermission, PERMISSIONS } from '../../utils/permissionUtils';
import { quotaService } from '../../services/quotaService';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
const AdminQuotas = () => {
  const { currentUser } = useContext(AppContext);
  const canManageQuotas = hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_QUOTAS_MANAGE);
  const [quotas, setQuotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedQuota, setSelectedQuota] = useState(null);
  const [quotaCounts, setQuotaCounts] = useState([]);
  const [countsLoading, setCountsLoading] = useState(true);
  const [exceededModalOpen, setExceededModalOpen] = useState(false);
  const exceededQuotas = useMemo(() => quotaCounts.filter(q => q.exceeded), [quotaCounts]);
  const [dialogState, setDialogState] = useState({
    create: false,
    edit: false,
    delete: false
  });

  const loadQuotaCounts = async () => {
    try {
      setCountsLoading(true);
      const data = await quotaService.getCounts();
      setQuotaCounts(data);
    } catch (err) {
      console.error('Failed to load quota counts:', err);
    } finally {
      setCountsLoading(false);
    }
  };

  const loadQuotas = async () => {
    try {
      setLoading(true);
      const minDelay = new Promise(resolve => setTimeout(resolve, 600));
      const [response] = await Promise.all([fetchWithAuth('/admin/quotas'), minDelay]);

      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }

      const json = await response.json();

      if (json.success) {
        setQuotas(json.data);
      } else {
        throw new Error(json.error || 'Failed to fetch quotas');
      }
    } catch (err) {
      console.error('Failed to load quotas:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuotas();
    loadQuotaCounts();
  }, []);

  const handleCreateQuota = () => {
    setDialogState({ create: true, edit: false, delete: false });
  };

  const handleEditQuota = (quota) => {
    setSelectedQuota(quota);
    setDialogState({ create: false, edit: true, delete: false });
  };

  const handleDeleteQuota = (quota) => {
    setSelectedQuota(quota);
    setDialogState({ create: false, edit: false, delete: true });
  };

  const handleCloseDialogs = () => {
    setDialogState({ create: false, edit: false, delete: false });
    setSelectedQuota(null);
  };

  const handleQuotaCreated = () => {
    loadQuotas();
    handleCloseDialogs();
  };

  const handleQuotaUpdated = () => {
    loadQuotas();
    handleCloseDialogs();
  };

  const handleQuotaDeleted = () => {
    loadQuotas();
    handleCloseDialogs();
  };

  // Extract a custom field value from quota filters
  const getFilterValue = (quota, fieldKey) => {
    const field = quota?.filters?.customFields?.find(f => f.fieldKey === fieldKey);
    if (!field) return null;
    return Array.isArray(field.value) ? field.value.join(', ') : field.value;
  };

  // Format window type for display
  const formatWindowType = (window) => {
    if (!window) return '-';
    switch (window.type) {
      case 'month':
        return 'Mensuel';
      case 'isoQuarter':
        return 'Trimestriel';
      case 'rollingDays':
        return `${window.days} jours glissants`;
      case 'fixed':
        return `Fixe (${window.from} - ${window.to})`;
      case 'none':
        return 'Aucune limite';
      default:
        return window.type;
    }
  };

  // Table columns definition
  const columns = useMemo(
    () => [
      {
        accessorFn: (row) => row.enabled ? "Actif" : "Inactif",
        id: "enabled",
        header: 'Statut',
        enableSorting: true,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {row.original.enabled ? (
              <>
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-green-600 text-sm font-medium">Actif</span>
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4 text-gray-400" />
                <span className="text-gray-400 text-sm">Inactif</span>
              </>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'serviceDeskName',
        header: 'Service Desk',
        enableSorting: true,
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium">{row.original.serviceDeskName}</span>
            <span className="text-xs text-gray-500">ID: {row.original.scope.serviceDeskId}</span>
          </div>
        ),
      },
      {
        accessorFn: (row) => row.filters?.requestType || '',
        id: 'requestType',
        header: 'Type de demande',
        enableSorting: true,
        cell: ({ row }) => {
          const name = row.original.filters?.requestType;
          return (
            <div className="flex flex-col">
              <span className="text-sm">{name || '-'}</span>
              <span className="text-xs text-gray-400">ID: {row.original.scope.requestTypeId}</span>
            </div>
          );
        },
      },
      {
        accessorFn: (row) => getFilterValue(row, 'brand') || '',
        id: 'brand',
        header: 'Marque',
        enableSorting: true,
        cell: ({ row }) => {
          const brand = getFilterValue(row.original, 'brand');
          return brand
            ? <Badge variant="outline" className="text-xs">{brand}</Badge>
            : <span className="text-gray-400 text-sm">-</span>;
        },
      },
      {
        accessorFn: (row) => getFilterValue(row, 'sector') || '',
        id: 'sector',
        header: 'Secteur',
        enableSorting: true,
        cell: ({ row }) => {
          const sector = getFilterValue(row.original, 'sector');
          return sector
            ? <Badge variant="secondary" className="text-xs">{sector}</Badge>
            : <span className="text-gray-400 text-sm">-</span>;
        },
      },
      {
        accessorFn: (row) => formatWindowType(row.window),
        id: 'window',
        header: 'Période',
        enableSorting: true,
        cell: ({ row }) => {
          return <span className="text-sm">{formatWindowType(row.original.window)}</span>
        },
      },
      {
        accessorKey: 'limit',
        header: 'Limite',
        enableSorting: true,
        cell: ({ row }) => (
          <span className="font-semibold text-sm">{row.original.limit}</span>
        ),
      },
      {
        accessorFn: (row) => {
          const quota = quotaCounts.find(eq => eq.id === row.id);
          return quota ? quota.count : 0;
        },
        id: 'exceeded',
        header: 'Atteint',
        enableSorting: true,
        cell: ({ row }) => {
          const quota = quotaCounts.find(eq => eq.id === row.original.id);
          if (!quota || quota.count === 0) {
            return (
              <div className="flex items-center gap-1.5">
                <span className="text-gray-500 text-sm font-medium">Aucun quota</span>
              </div>
            );
          }
          const percentage = Math.min((quota.count / quota.limit) * 100, 100);
          const isExceeded = quota.exceeded;
          return (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1.5">
                {isExceeded
                  ? <AlertTriangle className={`w-4 h-4 ${quota.behavior === 'BLOCK' ? 'text-red-500' : 'text-orange-500'}`} />
                  : <CheckCircle className="w-4 h-4 text-green-500" />
                }
                <span className={`text-sm font-semibold ${isExceeded ? (quota.behavior === 'BLOCK' ? 'text-red-600' : 'text-orange-600') : 'text-gray-700'}`}>
                  {quota.count} / {quota.limit}
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full ${isExceeded ? (quota.behavior === 'BLOCK' ? 'bg-red-500' : 'bg-orange-400') : 'bg-green-500'}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        },
      },
      {
        accessorFn: (row) => row?.onExceed?.behavior === 'BLOCK' ? 'Bloquer' : 'Avertir',
        id: 'onExceed.behavior',
        header: 'Action',
        enableSorting: true,
        cell: ({ row }) => (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.original.onExceed.behavior === 'BLOCK'
              ? 'bg-red-100 text-red-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            {row.original.onExceed.behavior === 'BLOCK' ? 'Bloquer' : 'Avertir'}
          </span>
        ),
      },
      ...(canManageQuotas ? [{
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex gap-2">
            <button
              onClick={() => handleEditQuota(row.original)}
              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
              title="Modifier"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDeleteQuota(row.original)}
              className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Supprimer"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ),
      }] : []),
    ],
    [canManageQuotas, quotaCounts]
  );

  const uniqueSectors = useMemo(() => {
    const sectorsMap = new Map();
    quotas.forEach(quota => {
      const sectorField = quota?.filters?.customFields?.find(field => field.fieldKey === 'sector');
      if (!sectorField) {
        return;
      }
      if (!sectorsMap.has(sectorField.value)) {
        sectorsMap.set(sectorField.value, {
          id: sectorField.value,
          name: sectorField.value
        });
      }
    });
    return Array.from(sectorsMap.values());
  }, [quotas.length]);

  const uniqueBrands = useMemo(() => {
    const brandsMap = new Map();
    quotas.forEach(quota => {
      const brandField = quota?.filters?.customFields?.find(field => field.fieldKey === 'brand');
      if (!brandField) {
        return;
      }
      const values = Array.isArray(brandField.value) ? brandField.value : [brandField.value];
      values.forEach(value => {
        if (!brandsMap.has(value)) {
          brandsMap.set(value, {
            id: value,
            name: value
          });
        }
      });
    });
    return Array.from(brandsMap.values());
  }, [quotas.length]);

  const renderCustomFilters = ({ table, columnFilters }) => {
    const sectorFilter = columnFilters.find(f => f.id === 'sector')?.value || 'all';
    const brandFilter = columnFilters.find(f => f.id === 'brand')?.value || 'all';

    return (
      <>
        <Select
          value={sectorFilter}
          onValueChange={(value) => {
            table.getColumn('sector')?.setFilterValue(value === 'all' ? undefined : value);
          }}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Tous les secteurs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les secteurs</SelectItem>
            {uniqueSectors.map((sector) => (
              <SelectItem key={sector.id} value={sector.name}>
                {sector.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={brandFilter}
          onValueChange={(value) => {
            table.getColumn('brand')?.setFilterValue(value === 'all' ? undefined : value);
          }}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Toutes les marques" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les marques</SelectItem>
            {uniqueBrands.map((brand) => (
              <SelectItem key={brand.id} value={brand.name}>
                {brand.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* <Button
          onClick={exportToCSV}
          variant="outline"
          className="flex items-center gap-2 whitespace-nowrap"
        >
          <Download className="w-4 h-4" />
          Exporter CSV
        </Button> */}
        <Button
          onClick={() => table.resetColumnFilters()}
          variant="outline"
          className="flex items-center gap-2 whitespace-nowrap"
        >
          <FilterX className="h-4 w-4" />
          Effacer les filtres
        </Button>
      </>
    );
  };

  // Calculate statistics
  const stats = {
    total: quotas.length,
    active: quotas.filter(q => q.enabled).length,
    inactive: quotas.filter(q => !q.enabled).length,
    blocking: quotas.filter(q => q.onExceed?.behavior === 'BLOCK').length
  };

  if (loading && quotas.length === 0) {
    return <AdminQuotasSkeleton />;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
        <h3 className="font-bold">Une erreur est survenue</h3>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="w-8 h-8" />
            Gestion des Quotas
          </h1>
          <p className="text-gray-500 mt-1">Créer, modifier ou supprimer des règles de quota</p>
        </div>
        {canManageQuotas && (
          <Button onClick={handleCreateQuota} size="lg" className="bg-black hover:bg-gray-800">
            <Plus className="w-4 h-4 mr-2" />
            Créer un quota
          </Button>
        )}
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Quotas</CardTitle>
            <Settings className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Toutes les règles configurées
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Actifs</CardTitle>
            <Badge className="h-4 px-2 bg-green-100 text-green-800">Actif</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Quotas en cours d'application
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Inactifs</CardTitle>
            <Badge variant="secondary" className="h-4 px-2">Inactif</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.inactive}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Quotas désactivés
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Bloquants</CardTitle>
            <Badge variant="destructive" className="h-4 px-2">Bloquer</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.blocking}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Quotas avec blocage
            </p>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-colors hover:border-orange-300 ${
            exceededQuotas.length > 0 ? 'border-orange-400 bg-orange-50/50' : ''
          }`}
          onClick={() => setExceededModalOpen(true)}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Quotas atteints</CardTitle>
            <AlertTriangle className={`h-4 w-4 ${exceededQuotas.length > 0 ? 'text-orange-500' : 'text-muted-foreground'}`} />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${exceededQuotas.length > 0 ? 'text-orange-600' : ''}`}>
              {countsLoading ? <Skeleton className="h-8 w-8" /> : exceededQuotas.length}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Quotas actuellement dépassés
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quotas Table */}
      <Card>
        <CardHeader>
          <CardTitle>Règles de quota configurées</CardTitle>
          <CardDescription>
            Visualisez, créez, modifiez ou supprimez des règles de quota
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            data={quotas}
            columns={columns}
            emptyMessage="Aucun quota configuré"
            showGlobalSearch={true}
            showPagination={true}
            pageSize={50}
            customFilters={renderCustomFilters}
          />
        </CardContent>
      </Card>

      {/* Dialogs */}
      <QuotaCreateDialog
        open={dialogState.create}
        onClose={handleCloseDialogs}
        onQuotaCreated={handleQuotaCreated}
      />

      <QuotaEditDialog
        open={dialogState.edit}
        quota={selectedQuota}
        onClose={handleCloseDialogs}
        onQuotaUpdated={handleQuotaUpdated}
      />

      <QuotaDeleteDialog
        open={dialogState.delete}
        quota={selectedQuota}
        onClose={handleCloseDialogs}
        onQuotaDeleted={handleQuotaDeleted}
      />

      {/* Exceeded Quotas Modal */}
      <Dialog open={exceededModalOpen} onOpenChange={setExceededModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Quotas atteints
            </DialogTitle>
            <DialogDescription>
              {exceededQuotas.length === 0
                ? 'Aucun quota n\'est actuellement dépassé.'
                : `${exceededQuotas.length} quota${exceededQuotas.length > 1 ? 's' : ''} actuellement dépassé${exceededQuotas.length > 1 ? 's' : ''}.`
              }
            </DialogDescription>
          </DialogHeader>

          {exceededQuotas.length > 0 && (
            <div className="space-y-3 mt-2">
              {exceededQuotas.map((eq) => (
                <div
                  key={eq.id}
                  className="border rounded-lg p-4 space-y-2 bg-white"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{eq.serviceDeskName}</span>
                      {eq.filters?.requestType && (
                        <span className="text-xs text-gray-500">{eq.filters.requestType}</span>
                      )}
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      eq.behavior === 'BLOCK'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {eq.behavior === 'BLOCK' ? 'Bloquer' : 'Avertir'}
                    </span>
                  </div>

                  {/* Brand / Sector badges */}
                  {eq.filters?.customFields?.length > 0 && (
                    <div className="flex items-center gap-2">
                      {eq.filters.customFields.map((f, i) => (
                        <Badge key={i} variant={f.fieldKey === 'brand' ? 'outline' : 'secondary'} className="text-xs">
                          {f.fieldKey === 'brand' ? 'Marque' : f.fieldKey === 'sector' ? 'Secteur' : f.fieldKey}: {Array.isArray(f.value) ? f.value.join(', ') : f.value}
                        </Badge>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1.5">
                      <span className="text-gray-500">Compteur:</span>
                      <span className="font-bold text-orange-600">{eq.count}</span>
                      <span className="text-gray-400">/</span>
                      <span className="font-medium">{eq.limit}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-gray-500">Période:</span>
                      <span>{formatWindowType(eq.window)}</span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        eq.behavior === 'BLOCK' ? 'bg-red-500' : 'bg-orange-400'
                      }`}
                      style={{ width: `${Math.min((eq.count / eq.limit) * 100, 100)}%` }}
                    />
                  </div>

                  {eq.window.from && eq.window.to && (
                    <p className="text-xs text-gray-400">
                      Fenêtre: {eq.window.from} — {eq.window.to}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminQuotas;
